import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { PageLayout } from '@/components/layout/PageLayout'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { useQueryClient } from '@tanstack/react-query'
import {
  useGetApiCompaniesCurrentEmployeesEmployeeId,
  usePostApiCompaniesCurrentEmployeesEmployeeIdTransactionsBook,
  getGetApiCompaniesCurrentEmployeesEmployeeIdQueryKey,
} from '@/api/generated/company-management-user/company-management-user'
import { useDeleteApiCompaniesCurrentEmployeesEmployeeIdPassword } from '@/api/generated/public/public'
import { notify } from '@/lib/notify'
import { ServiceModuleType } from '@/api/model'
import type { ActionModuleResponse, BaseShopModuleModel, ServiceModuleModel } from '@/api/model'
import { EmployeeDetailActions } from './detail-page-components/EmployeeDetailActions'
import { EmployeeInfoCard } from './detail-page-components/EmployeeInfoCard'
import { EmployeeWorkTimeCard } from './detail-page-components/EmployeeWorkTimeCard'
import { EmployeeVacationCard } from './detail-page-components/EmployeeVacationCard'
import { EmployeeModulesSection } from './detail-page-components/EmployeeModulesSection'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'

export default function EmployeeDetailsPage() {
  const { t } = useTranslation('employees')
  const { employeeId } = useParams<{ employeeId: string }>()
  const queryClient = useQueryClient()
  const [confirmReset, setConfirmReset] = useState(false)
  const [confirmBook, setConfirmBook] = useState(false)

  const { data: response, isLoading, isError } = useGetApiCompaniesCurrentEmployeesEmployeeId(
    employeeId ?? '',
    { query: { enabled: !!employeeId } }
  )
  const employee = response?.data

  const { mutate: resetPassword, isPending: isResetting } =
    useDeleteApiCompaniesCurrentEmployeesEmployeeIdPassword({
      mutation: {
        onSuccess: () => {
          notify.success(t('toasts.passwordReset'))
          setConfirmReset(false)
        },
        onError: (error) => notify.apiError(error, t('toasts.passwordResetError')),
      },
    })

  const { mutate: bookTransaction, isPending: isBooking } =
    usePostApiCompaniesCurrentEmployeesEmployeeIdTransactionsBook({
      mutation: {
        onSuccess: () => {
          notify.success(t('toasts.pointsBooked'))
          setConfirmBook(false)
          if (employeeId) {
            queryClient.invalidateQueries({
              queryKey: getGetApiCompaniesCurrentEmployeesEmployeeIdQueryKey(employeeId),
            })
          }
        },
        onError: (error) => notify.apiError(error, t('toasts.pointsBookError')),
      },
    })

  const fullName = [employee?.firstName, employee?.lastName].filter(Boolean).join(' ')
  const shopModules = (employee?.shopModules ?? []) as BaseShopModuleModel[]
  const actionModules = (employee?.actionModules ?? []) as ActionModuleResponse[]
  const serviceModules = (employee?.serviceModules ?? []) as ServiceModuleModel[]
  const timeTrackingModuleId = serviceModules.find((m) => m.type === ServiceModuleType.Timetracking)?.id
  const absenceModuleId = serviceModules.find((m) => m.type === ServiceModuleType.Absence)?.id

  return (
    <PageLayout scrollEnabled>
      <LoaderGeneral isLoading={isLoading} isError={isError}>
        <EmployeeDetailActions
          onResetPassword={() => setConfirmReset(true)}
          onBookPoints={() => setConfirmBook(true)}
          walletId={employee?.walletId}
        />

        <EmployeeInfoCard 
          fullName={fullName}
          position={employee?.position}
          status={employee?.status}
          registrationCompleted={employee?.registrationCompleted}
          employeeNumber={employee?.employeeNumber}
          birthDate={employee?.birthDate}
          employmentDate={employee?.employmentDate}
          subCompanyName={employee?.subCompanyName}
          groups={employee?.groups}
          registrationCode={employee?.registrationCode}
          onboardingFileDownloadUrl={employee?.onboardingFileDownloadUrl}
        />

        {(timeTrackingModuleId != null || absenceModuleId != null) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {timeTrackingModuleId != null && (
              <EmployeeWorkTimeCard serviceModuleId={timeTrackingModuleId} employeeId={employee?.id ?? ''} />
            )}
            {absenceModuleId != null && (
              <EmployeeVacationCard serviceModuleId={absenceModuleId} employeeId={employee?.id ?? ''} />
            )}
          </div>
        )}

        <EmployeeModulesSection
          actionModules={actionModules}
          serviceModules={serviceModules}
          shopModules={shopModules}
        />
      </LoaderGeneral>

      <ConfirmDialog
        open={confirmReset}
        title={t('detail.resetConfirm.title')}
        description={t('detail.resetConfirm.description', { name: fullName })}
        confirmLabel={t('buttons.reset', { ns: 'common' })}
        confirmButtonVariant="destructive"
        isLoading={isResetting}
        onConfirm={() => employeeId && resetPassword({ employeeId })}
        onClose={() => setConfirmReset(false)}
      />

      <ConfirmDialog
        open={confirmBook}
        title={t('detail.bookConfirm.title')}
        description={t('detail.bookConfirm.description', { name: fullName })}
        confirmLabel={t('detail.bookConfirm.confirmLabel')}
        isLoading={isBooking}
        onConfirm={() => employeeId && bookTransaction({ employeeId })}
        onClose={() => setConfirmBook(false)}
      />
    </PageLayout>
  )
}
